import { useQuery } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import {
  buildAcquisitionAnalyticsParams,
  type AcquisitionSourceFilter,
} from "../acquisition/acquisitionSourceFilter";

type StoredToken = { AccessToken?: string };

export type FinancialsKpiCardsFilters = AcquisitionSourceFilter & {
  start_date: string;
  end_date: string;
};

export type FinancialsKpiCardData = {
  value: number;
  previous_value: number;
  percent_change: number | null;
};

export type FinancialsKpiCardsResponse = {
  deposit_amount: FinancialsKpiCardData;
  withdrawal_amount: FinancialsKpiCardData;
  revenue: FinancialsKpiCardData;
  profitability_pct: FinancialsKpiCardData;
  active_deposit_users: FinancialsKpiCardData;
  total_cash_balance: FinancialsKpiCardData;
  non_wagered_cash: FinancialsKpiCardData;
  redeemable_cash: FinancialsKpiCardData;
};

export const fetchFinancialsKpiCards = async (
  filters: FinancialsKpiCardsFilters,
): Promise<FinancialsKpiCardsResponse> => {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString
    ? JSON.parse(tokenString)
    : null;
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);

  const response = await instance.get(API_ROUTES.FINANCIALS_KPI_CARDS, {
    params: buildAcquisitionAnalyticsParams(filters),
  });

  const data = response?.data?.data;
  if (!data) {
    throw new Error("Invalid response: missing KPI data");
  }
  return data;
};

export const useGetFinancialsKpiCards = (
  filters: FinancialsKpiCardsFilters,
) => {
  const { start_date, end_date, source } = filters;

  return useQuery<FinancialsKpiCardsResponse, Error>({
    queryKey: [
      "FINANCIALS_KPI_CARDS",
      start_date,
      end_date,
      source?.join(","),
    ],
    queryFn: () => fetchFinancialsKpiCards(filters),
    enabled: Boolean(start_date && end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
